import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { state } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import type { ProjectTemplate, ChatGenerateOptions, ChatSession, ChatMessage } from "../../types/ead-types.js";
import { templateFromListSummary } from "../template-list-cache.ts";

@customElement("ead-generate-chat-view")
export class EadGenerateChatView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; }
    .generate-container { display: flex; height: 100%; position: relative; z-index: 1; }

    .sidebar { width: 320px; min-width: 320px; border-right: 1px solid var(--border, #2e2f33); display: flex; flex-direction: column; background: var(--bg-surface, #222326); }
    .sidebar-header { padding: 16px 20px; border-bottom: 1px solid var(--border, #2e2f33); }
    .sidebar-header h2 { margin: 0; font-size: 15px; font-weight: 600; }
    .template-list { flex: 1; overflow-y: auto; }
    .template-item { padding: 12px 20px; border-bottom: 1px solid var(--border, #2e2f33); cursor: pointer; transition: background 0.1s; }
    .template-item:hover { background: rgba(255,255,255,0.03); }
    .template-item.selected { background: rgba(255,255,255,0.06); border-left: 3px solid var(--accent, #6ea8fe); }
    .template-item .name { font-size: 13px; font-weight: 500; color: var(--fg, #e4e5e7); }
    .template-item .description { font-size: 11px; color: var(--muted, #8b8d93); margin-top: 4px; }

    .main-content { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
    .config-panel { padding: 20px 24px; border-bottom: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326); position: relative; z-index: 10; pointer-events: auto; }
    .config-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 16px; }
    .form-group { }
    .form-group label { display: block; font-size: 11px; font-weight: 500; color: var(--muted, #8b8d93); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
    .form-group input, .form-group select, .form-group textarea {
      width: 100%;
      min-height: 36px;
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg, #1a1b1e);
      color: var(--fg, #e4e5e7);
      font-size: 13px;
      font-family: inherit;
      pointer-events: auto !important;
      cursor: text;
      position: relative;
      z-index: 100;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
      outline: none;
      border-color: var(--accent, #6ea8fe);
      pointer-events: auto !important;
      z-index: 101;
    }
    .form-group input:disabled, .form-group select:disabled, .form-group textarea:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
    .config-panel {
      pointer-events: auto !important;
      position: relative;
      z-index: 10;
    }
    .config-grid {
      pointer-events: auto !important;
      position: relative;
      z-index: 11;
      display: grid !important;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
    }
    .form-group {
      pointer-events: auto !important;
      position: relative;
      z-index: 12;
      display: block !important;
    }
    .form-group label {
      display: block !important;
      visibility: visible !important;
    }
    .form-group textarea { resize: vertical; min-height: 60px; }

    .action-bar { display: flex; gap: 8px; align-items: center; }
    .btn { padding: 8px 16px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--fg, #e4e5e7); font-size: 13px; cursor: pointer; font-weight: 500; }
    .btn:hover { background: rgba(255,255,255,0.05); }
    .btn-primary { background: var(--accent, #6ea8fe); color: white; border-color: var(--accent, #6ea8fe); }
    .btn-primary:hover { background: #5a9aef; }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .preview-panel { flex: 1; overflow-y: auto; padding: 24px; }
    .preview-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
    .preview-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
    .chat-preview { background: var(--bg-surface, #222326); border-radius: 8px; border: 1px solid var(--border, #2e2f33); overflow: hidden; }
    .chat-message { padding: 12px 16px; border-bottom: 1px solid var(--border, #2e2f33); }
    .chat-message:last-child { border-bottom: none; }
    .chat-message .role { font-size: 11px; color: var(--muted, #8b8d93); font-weight: 500; margin-bottom: 4px; }
    .chat-message .content { font-size: 13px; line-height: 1.5; color: var(--fg, #e4e5e7); }

    .loading { display: flex; align-items: center; justify-content: center; height: 200px; color: var(--muted, #8b8d93); }
    .empty { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted, #8b8d93); flex-direction: column; gap: 8px; }

    .spinner { width: 24px; height: 24px; border: 2px solid var(--border, #2e2f33); border-top-color: var(--accent, #6ea8fe); border-radius: 50%; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }

    .export-options { display: flex; gap: 8px; }
    .export-btn { padding: 6px 12px; border-radius: 4px; border: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326); color: var(--fg, #e4e5e7); font-size: 11px; cursor: pointer; }
    .export-btn:hover { background: rgba(255,255,255,0.05); }
  `;

  @state() private _templates: ProjectTemplate[] = [];
  @state() private _selectedTemplate: ProjectTemplate | null = null;
  @state() private _generating = false;
  @state() private _generatedSession: ChatSession | null = null;
  @state() private _loading = true;

  // Chat generation options
  @state() private _focusArea = '';
  @state() private _maxLength = 20;
  @state() private _style: 'technical' | 'conversational' | 'formal' = 'conversational';
  @state() private _includeContext = true;

  async connectedCallback() {
    super.connectedCallback();
    console.log('[GenerateChat] Component connected');
    try {
      await this._loadTemplates();
      console.log('[GenerateChat] Templates loaded:', this._templates.length);
    } catch (error) {
      console.error('[GenerateChat] Failed to load templates:', error);
    }
  }

  private _handleFocusInput(e: Event) {
    const target = e.target as HTMLInputElement;
    console.log('[GenerateChat] Focus area input:', target.value);
    this._focusArea = target.value;
  }

  private _handleMaxLengthInput(e: Event) {
    const target = e.target as HTMLInputElement;
    console.log('[GenerateChat] Max length input:', target.value);
    this._maxLength = parseInt(target.value) || 20;
  }

  private _handleStyleChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    console.log('[GenerateChat] Style changed:', target.value);
    this._style = target.value as any;
  }

  private _handleContextChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    console.log('[GenerateChat] Context changed:', target.value);
    this._includeContext = target.value === 'true';
  }

  private async _loadTemplates() {
    this._loading = true;
    const result = await hermesClient.request<{ templates: Array<Record<string, unknown>> }>("templates.list");
    if (result?.templates) {
      this._templates = result.templates.map((row) => templateFromListSummary(row));
      if (this._templates.length > 0 && !this._selectedTemplate) {
        this._selectedTemplate = this._templates[0];
      }
    }
    this._loading = false;
  }

  private async _generateChat() {
    if (!this._selectedTemplate || this._generating) return;

    this._generating = true;
    this._generatedSession = null;

    try {
      const options: ChatGenerateOptions = {
        templateId: this._selectedTemplate.id,
        focusArea: this._focusArea || undefined,
        maxLength: this._maxLength,
        style: this._style,
        includeContext: this._includeContext,
      };

      const result = await hermesClient.request<{ session: ChatSession }>("chat.generate", options);
      if (result?.session) {
        this._generatedSession = result.session;
      }
    } catch (error) {
      console.error('[GenerateChat] Failed to generate chat:', error);
    } finally {
      this._generating = false;
    }
  }

  private async _exportChat(format: 'md' | 'pdf' | 'json') {
    if (!this._generatedSession) return;

    try {
      const result = await hermesClient.request<{ url: string }>("chat.export", {
        sessionId: this._generatedSession.sessionId,
        format,
      });

      if (result?.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      console.error('[GenerateChat] Failed to export chat:', error);
    }
  }

  private _renderTemplateList() {
    if (this._loading) return html`<div class="loading"><div class="spinner"></div></div>`;
    if (this._templates.length === 0) return html`<div class="empty">No templates found. Create one in Projects.</div>`;

    return this._templates.map(template => html`
      <div class="template-item ${this._selectedTemplate?.id === template.id ? 'selected' : ''}"
           @click=${() => { this._selectedTemplate = template; }}>
        <div class="name">${template.name}</div>
        <div class="description">${template.description || 'No description'}</div>
      </div>
    `);
  }

  render() {
    return html`
      <div class="generate-container">
        <div class="sidebar">
          <div class="sidebar-header">
            <h2>Select Template</h2>
          </div>
          <div class="template-list">
            ${this._renderTemplateList()}
          </div>
        </div>

        <div class="main-content">
          <div class="config-panel">
            <h3 style="margin: 0 0 16px; font-size: 14px; font-weight: 600;">Chat Generation Options</h3>

            <div class="config-grid">
              <div class="form-group">
                <label>Focus Area (Optional)</label>
                <input type="text" placeholder="e.g., authentication, PFM nodes" .value=${this._focusArea}
                       @input=${this._handleFocusInput}>
                <div style="font-size: 11px; color: var(--muted); margin-top: 4px;">Current: "${this._focusArea}"</div>
              </div>

              <div class="form-group">
                <label>Max Length</label>
                <input type="number" min="5" max="50" .value=${this._maxLength}
                       @input=${this._handleMaxLengthInput}>
                <div style="font-size: 11px; color: var(--muted); margin-top: 4px;">Messages: ${this._maxLength}</div>
              </div>

              <div class="form-group">
                <label>Style</label>
                <select .value=${this._style} @change=${this._handleStyleChange}>
                  <option value="conversational">Conversational</option>
                  <option value="technical">Technical</option>
                  <option value="formal">Formal</option>
                </select>
              </div>

              <div class="form-group">
                <label>Include Context</label>
                <select .value=${this._includeContext ? 'true' : 'false'} @change=${this._handleContextChange}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            <div class="action-bar">
              <button class="btn btn-primary"
                      ?disabled=${!this._selectedTemplate || this._generating}
                      @click=${this._generateChat}
                      style="padding: 12px 24px; font-size: 14px;">
                ${this._generating ? html`<div class="spinner"></div> Generating...` : '🚀 Generate Chat'}
              </button>
              <div style="margin-left: 12px; font-size: 12px; color: var(--muted);">
                ${this._selectedTemplate ? `Template: ${this._selectedTemplate.name}` : 'No template selected'}
              </div>
              ${this._generatedSession ? html`
                <div class="export-options">
                  <button class="export-btn" @click=${() => this._exportChat('md')}>Export MD</button>
                  <button class="export-btn" @click=${() => this._exportChat('pdf')}>Export PDF</button>
                  <button class="export-btn" @click=${() => this._exportChat('json')}>Export JSON</button>
                </div>
              ` : ''}
            </div>
          </div>

          <div class="preview-panel">
            ${this._generating
              ? html`<div class="loading"><div class="spinner"></div> Generating chat conversation...</div>`
              : this._generatedSession
                ? this._renderPreview()
                : html`<div class="empty">
                    <div style="font-size: 40px; margin-bottom: 16px;">💬</div>
                    <div style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">Ready to Generate Chat</div>
                    <div style="font-size: 13px; color: var(--muted); max-width: 400px; text-align: center;">
                      1. Select a template from the left sidebar<br>
                      2. Configure options above (optional)<br>
                      3. Click "🚀 Generate Chat" button
                    </div>
                  </div>`
            }
          </div>
        </div>
      </div>
    `;
  }

  private _renderPreview() {
    if (!this._generatedSession) return html``;

    return html`
      <div class="preview-header">
        <h3>Generated Chat (${this._generatedSession.messages.length} messages)</h3>
        <div style="font-size: 12px; color: var(--muted);">
          ${new Date(this._generatedSession.generatedAt).toLocaleString()}
        </div>
      </div>

      <div class="chat-preview">
        ${this._generatedSession.messages.map(msg => this._renderMessage(msg))}
      </div>
    `;
  }

  private _renderMessage(msg: ChatMessage) {
    return html`
      <div class="chat-message">
        <div class="role">${msg.label || msg.role}</div>
        <div class="content" .innerHTML=${toSanitizedMarkdownHtml(msg.content)}></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-generate-chat-view": EadGenerateChatView;
  }
}
